/**
 * W1-11b (app-shell-ui): the `@` palette provider (DESIGN.md §8.4): go to a beat (`@37:1/4`, `@12.5`, relative
 * `@+4`), a time (`@1:23.5`, `@23.5s`) or a marker by name (`@drop`). Beats use the beat-field syntax of the
 * settings (core/time parseBeat: snap divisor, default or RPE decimal meaning); a number that is both a beat and a
 * time offers both. Running an item reveals the position (`nav.reveal`: seek + scroll).
 */

import type { Beat } from '../../core/time/types';
import { Beats } from '../../core/time/beat';
import { formatSec, parseBeat } from '../../core/time/beatSyntax';
import type { AppServices, PaletteItem, PaletteProvider } from '../registry/types';
import { ref } from './commandState';
import { rank } from './paletteSearch';
import { beatArg, displayBeat, playheadSec, tempoOf } from './playback';
import { m } from './shell.messages';

const MAX_MARKERS = 50;

function reveal(s: AppServices, b: Beat): void {
  void s.registry.runAny('nav.reveal', { beat: beatArg(b) }, 'palette');
}

export function timePaletteProvider(s: AppServices): PaletteProvider {
  return {
    prefix: '@',
    search(query) {
      const store = s.store;
      if (store === null) return [];
      const doc = store.doc;
      const tempo = tempoOf(store);
      const editor = s.editor.getState();
      const style = s.settings.value.editing.beatDisplay;
      const markers = [...doc.markers.values()].sort((a, b) => Beats.cmp(a.t, b.t));
      const markerByName = (name: string): Beat | undefined => {
        const exact = markers.find((x) => x.name === name);
        if (exact !== undefined) return exact.t;
        const n = Number(name);
        return Number.isInteger(n) && n >= 1 ? markers[n - 1]?.t : undefined;
      };
      const current = tempo.beatAt(playheadSec(s));
      const ctx = {
        snapDiv: editor.snap.div,
        syntax: s.settings.value.editing.beatSyntax,
        current,
        tempo,
        marker: markerByName,
      } as const;
      const fmt = (b: Beat): string => displayBeat(b, style, store, (r) => s.i18n.tr(r));
      const items: PaletteItem[] = [];
      const seen = new Set<string>();
      const add = (b: Beat, kind: 'beat' | 'time', relative: boolean): void => {
        const key = Beats.key(b);
        if (seen.has(key)) return;
        seen.add(key);
        const params = { beat: fmt(b), time: formatSec(tempo.secAt(b)) };
        items.push({
          id: `@${kind}:${key}`,
          title: ref(
            kind === 'time' ? m.paletteGotoTime : relative ? m.paletteGotoRelative : m.paletteGotoBeat,
            params,
          ),
          detail: ref(m.paletteGotoDetail, params),
          icon: kind === 'time' ? 'Clock' : 'Crosshair',
          run: () => {
            reveal(s, b);
          },
        });
      };
      const q = query.trim();
      if (q !== '') {
        const asBeat = parseBeat(q, ctx);
        if (asBeat.ok && Beats.cmp(asBeat.beat, Beats.ZERO) >= 0)
          add(asBeat.beat, /[s]$|^@/.test(q) ? 'time' : 'beat', asBeat.relative);
        if (!q.startsWith('@')) {
          const asTime = parseBeat(`@${q}`, ctx);
          if (asTime.ok && Beats.cmp(asTime.beat, Beats.ZERO) >= 0) add(asTime.beat, 'time', false);
        }
      }
      const matched = q === '' ? markers : rank(markers, q, (mk) => ({ title: mk.name }));
      for (const mk of matched.slice(0, MAX_MARKERS)) {
        const params = { name: mk.name, beat: fmt(mk.t), time: formatSec(tempo.secAt(mk.t)) };
        items.push({
          id: `@marker:${String(mk.id)}`,
          title: ref(m.paletteMarker, params),
          detail: ref(m.paletteGotoDetail, params),
          icon: 'Bookmark',
          run: () => {
            reveal(s, mk.t);
          },
        });
      }
      return items;
    },
  };
}
